import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Download, Copy, Wand2, AlertCircle, RotateCcw, Printer, FileText, GitCompare, Wifi, Bookmark, BookmarkPlus, X, Loader2, Share2, QrCode, Volume2, VolumeX, Star, Mail } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { CustomizationPanel } from './CustomizationPanel';
import { QrContentPanel } from './QrContentPanel';
import { useLang } from '../i18n';
import { useReviews } from '../reviews';
import { useAuth } from '../auth';
import { FREE_DAILY_LIMIT, freeRemaining, consumeFree } from '../freeQuota';
import api from '../api';
import { DEFAULT_OPTIONS, downloadDataUrl, safeName, printImages, printWifiCard } from '../utils';
import { generateSingleBarcodePdf, ensurePng } from '../pdf';
import { track } from '../analytics';
import { sendTrustpilotInvitation, isValidEmail } from '../trustpilot';

// URL-safe base64 encode/decode for sharing a preset config via link.
const encodePreset = (obj) => {
  try {
    const json = JSON.stringify(obj);
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (e) { return ''; }
};
const decodePreset = (s) => {
  try {
    let b64 = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch (e) { return null; }
};

// Live before/after comparison for the human-readable caption. Renders two
// thumbnails: the standard caption ("before") vs the current bold/scaled
// caption ("after"). Only rendered while a caption enhancement is active.
const ComparisonPreview = ({ typeId, value, baseOptions, currentOptions }) => {
  const { t } = useLang();
  const [imgs, setImgs] = useState({ before: null, after: null });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const baseKey = JSON.stringify(baseOptions);
  const curKey = JSON.stringify(currentOptions);

  useEffect(() => {
    if (!value.trim()) { setImgs({ before: null, after: null }); return; }
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [b, a] = await Promise.all([
          api.generate({ type: typeId, data: value, format: 'png', options: baseOptions }),
          api.generate({ type: typeId, data: value, format: 'png', options: currentOptions }),
        ]);
        setImgs({ before: b.data.data_url, after: a.data.data_url });
      } catch (e) {
        setImgs({ before: null, after: null });
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, value, baseKey, curKey]);

  return (
    <Card className="mt-4" data-testid="barcode-comparison-container">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <GitCompare className="h-4 w-4 text-primary" />{t('comparison')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('comparison_hint')}</p>
        <div className="grid grid-cols-2 gap-3">
          {['before', 'after'].map((slot) => (
            <div key={slot} className="space-y-1.5">
              <span
                className="block text-center text-xs font-medium text-muted-foreground"
                data-testid={`comparison-${slot}-label`}
              >
                {slot === 'before' ? t('comparison_before') : t('comparison_after')}
              </span>
              <div className="barcode-checker flex min-h-[120px] items-center justify-center rounded-lg border p-3">
                {loading ? (
                  <Skeleton className="h-16 w-full" />
                ) : imgs[slot] ? (
                  <div className="rounded bg-white p-2">
                    <img
                      src={imgs[slot]}
                      alt={slot}
                      data-testid={`comparison-${slot}-image`}
                      className="max-h-[140px] max-w-full"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const GenerateTab = ({ types, lang, onSaved }) => {
  const { t } = useLang();
  const { summary, nudgeAfterDownload } = useReviews();
  const { user, promptLogin, entitlement, openPricing, refresh } = useAuth();
  // Viewing/live-preview is free; producing an output (download/export/print/
  // save/share) requires a (free) account. Returns false and opens the login
  // dialog when the visitor is not signed in.
  const ensureAuth = () => {
    if (!user) { promptLogin(); return false; }
    return true;
  };
  // Guests get up to a (server-enforced, admin-configurable) number of free
  // generations/day. The server counts by IP so clearing the browser cache
  // does not reset it; localStorage is only an offline fallback.
  const [freeLimit, setFreeLimit] = useState(FREE_DAILY_LIMIT);
  const [freeLeft, setFreeLeft] = useState(() => freeRemaining());
  useEffect(() => {
    if (user) return;
    let ok = true;
    api.getQuota()
      .then((r) => { if (ok && r.data && !r.data.unlimited) { setFreeLimit(r.data.limit); setFreeLeft(r.data.remaining); } })
      .catch(() => { /* keep local fallback */ });
    return () => { ok = false; };
  }, [user]);

  const quotaLimitMsg = () => (lang === 'en'
    ? `You have used your ${freeLimit} free generations for today. Sign in for unlimited access.`
    : `Du hast deine ${freeLimit} kostenlosen Generierungen für heute aufgebraucht. Melde dich an für unbegrenzten Zugriff.`);

  const ensureQuota = async () => {
    if (user) {
      // Points gate for signed-in users. Only hard-block when Premium sales are
      // live; while "coming soon" the export proceeds (soft) so the app stays usable.
      if (!user.points_unlimited && entitlement?.plan !== 'lifetime' && (user.points ?? 0) <= 0 && salesEnabled) {
        toast.info(lang === 'en'
          ? 'Your points are used up — top up to keep exporting.'
          : 'Deine Punkte sind aufgebraucht – lade dein Guthaben auf.');
        try { openPricing(); } catch (e) { /* ignore */ }
        return false;
      }
      return true;
    }
    try {
      const res = await api.consumeQuota();
      const d = res.data || {};
      if (d.unlimited) return true;
      if (typeof d.limit === 'number') setFreeLimit(d.limit);
      setFreeLeft(Math.max(0, d.remaining || 0));
      if (!d.allowed) { promptLogin(quotaLimitMsg()); return false; }
      return true;
    } catch (e) {
      // Offline fallback: device-cache counting.
      if (freeRemaining() <= 0) { setFreeLeft(0); promptLogin(quotaLimitMsg()); return false; }
      setFreeLeft(consumeFree());
      return true;
    }
  };
  const [typeId, setTypeId] = useState('qrcode');
  const [value, setValue] = useState('');
  const [qrContent, setQrContent] = useState('text');
  const [qrName, setQrName] = useState('');
  const [qrMeta, setQrMeta] = useState(null);
  const [format, setFormat] = useState('png');
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [salesEnabled, setSalesEnabled] = useState(false);
  useEffect(() => {
    let ok = true;
    api.getPremiumConfig().then((r) => { if (ok) setSalesEnabled(!!r.data?.sales_enabled); }).catch(() => {});
    return () => { ok = false; };
  }, []);
  const debounceRef = useRef(null);
  const presetRef = useRef(false);
  const genTrackRef = useRef(0);

  // --- Presets (favorites) ------------------------------------------------
  const [presets, setPresets] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);
  const importRef = useRef(false);
  // Share-link QR dialog
  const [qrPreset, setQrPreset] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);

  const buildPresetLink = (p) => {
    const enc = encodePreset({ name: p.name, ...(p.config || {}) });
    if (!enc) return '';
    return `${window.location.origin}${window.location.pathname}?preset=${enc}`;
  };

  const loadPresets = async () => {
    try {
      const res = await api.getPresets();
      setPresets((res.data && res.data.presets) || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadPresets(); }, []);

  // Import a shared preset from the URL (?preset=...) once types are available.
  useEffect(() => {
    if (importRef.current || !types.length) return;
    importRef.current = true;
    try {
      const params = new URLSearchParams(window.location.search);
      const enc = params.get('preset');
      if (enc) {
        const cfg = decodePreset(enc);
        if (cfg && cfg.type) {
          applyPreset({ config: cfg }, { silent: true });
          if (cfg.name) setPresetName(cfg.name);
          track('preset_opened_from_link', { type: cfg.type });
          setTimeout(() => toast.success(t('preset_from_link')), 300);
        }
        params.delete('preset');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (e) { /* ignore malformed links */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const handleSavePreset = async () => {
    if (!ensureAuth()) return;
    const name = presetName.trim();
    if (!name) { toast.error(t('preset_name_required')); return; }
    setSavingPreset(true);
    try {
      const config = { type: typeId, value, format, options, qrContent, qrName };
      await api.savePreset({ name, config });
      await loadPresets();
      setSaveOpen(false);
      setPresetName('');
      track('preset_saved', { type: typeId });
      toast.success(t('preset_saved'));
    } catch (e) {
      toast.error(t('preset_error'));
    } finally {
      setSavingPreset(false);
    }
  };

  const applyPreset = (p, opts = {}) => {
    const c = (p && p.config) || {};
    setError('');
    if (c.type && types.some((x) => x.id === c.type)) setTypeId(c.type);
    if (c.options) setOptions({ ...DEFAULT_OPTIONS, ...c.options });
    if (c.format) setFormat(c.format);
    setQrContent(c.qrContent || 'text');
    setQrName(c.qrName || '');
    setQrMeta(null);
    if (typeof c.value === 'string') setValue(c.value);
    if (!opts.silent) { track('preset_loaded', { source: opts.source || 'chip' }); toast.success(t('preset_loaded')); }
  };

  const copyLink = async (url) => {
    if (!url) { toast.error(t('preset_error')); return false; }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('preset_link_copied'));
      return true;
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        toast.success(t('preset_link_copied'));
        return true;
      } catch (e2) {
        toast.error(t('preset_error'));
        return false;
      }
    }
  };

  const sharePreset = (p) => { track('preset_shared', { method: 'link' }); return copyLink(buildPresetLink(p)); };

  const openQr = async (p) => {
    const link = buildPresetLink(p);
    if (!link) { toast.error(t('preset_error')); return; }
    track('preset_shared', { method: 'qr' });
    setQrPreset({ ...p, _link: link });
    setQrDataUrl('');
    setQrLoading(true);
    try {
      const res = await api.generate({
        type: 'qrcode',
        data: link,
        format: 'png',
        options: { fg_color: '#000000', bg_color: '#ffffff', scale: 8, border: 2, error_correction: 'M' },
      });
      setQrDataUrl(res.data.data_url);
    } catch (e) {
      toast.error(t('preset_error'));
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQr = () => {
    if (!ensureAuth()) return;
    if (!qrDataUrl || !qrPreset) return;
    downloadDataUrl(qrDataUrl, `${safeName(qrPreset.name || 'vorlage')}_qr.png`);
    track('barcode_exported', { type: 'qrcode', format: 'png', source: 'qr_preset' });
    nudgeAfterDownload(t);
  };

  const [qrPdfLoading, setQrPdfLoading] = useState(false);
  const downloadQrPdf = async () => {
    if (!ensureAuth()) return;
    if (!qrDataUrl || !qrPreset) return;
    setQrPdfLoading(true);
    try {
      const png = await ensurePng(qrDataUrl);
      await generateSingleBarcodePdf(png, {
        caption: qrPreset.name || 'Vorlage',
        filename: `${safeName(qrPreset.name || 'vorlage')}_qr.pdf`,
      });
      track('barcode_exported', { type: 'qrcode', format: 'pdf', source: 'qr_preset' });
      nudgeAfterDownload(t);
    } catch (e) {
      toast.error(t('err_generate'));
    } finally {
      setQrPdfLoading(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!presetToDelete) return;
    const id = presetToDelete.id;
    setPresetToDelete(null);
    try {
      await api.deletePreset(id);
      setPresets((list) => list.filter((p) => p.id !== id));
      toast.success(t('preset_deleted'));
    } catch (e) {
      toast.error(t('preset_error'));
    }
  };


  // One-time: preselect barcode type from the URL (?type=) so SEO landing-page
  // CTAs open the generator on the right type.
  useEffect(() => {
    if (presetRef.current || !types.length) return;
    presetRef.current = true;
    try {
      const p = new URLSearchParams(window.location.search).get('type');
      if (p && types.some((x) => x.id === p)) setTypeId(p);
    } catch (e) { /* ignore */ }
  }, [types]);

  const currentType = useMemo(() => types.find((x) => x.id === typeId), [types, typeId]);
  const isQr = currentType?.category === '2d';
  const isQrCode = currentType?.id === 'qrcode';
  const supportsText = currentType?.supports_text ?? true;

  const grouped = useMemo(() => ({
    twod: types.filter((x) => x.category === '2d'),
    oned: types.filter((x) => x.category === '1d'),
  }), [types]);

  const runGenerate = async (opts = {}) => {
    if (!value.trim()) {
      setResult(null);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.generate({ type: typeId, data: value, format, options });
      setResult(res.data);
      // Throttled "barcode created" signal (live preview fires often).
      const now = Date.now();
      if (now - genTrackRef.current > 3000) {
        genTrackRef.current = now;
        track('barcode_generated', { type: typeId, format });
      }
      if (opts.save && onSaved) onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('err_generate'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Debounced live preview
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runGenerate({ save: false }), 450);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, value, format, options]);

  // Delegates to the shared, session-limited review nudge (see reviews.js) so
  // the prompt is consistent across all download/export paths. Also fires an
  // (optional) Trustpilot review invitation when the user provided an email.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  // Consume 1 generation point per export. Lifetime/unlimited users are exempt.
  // On an empty balance we open the top-up dialog (the export itself is not
  // hard-blocked while purchasing is still "coming soon").
  const consumeOnePoint = () => {
    if (!user) return;
    if (entitlement?.plan === 'lifetime') return;
    api.consumePoints(1)
      .then((r) => {
        try { refresh(); } catch (e) { /* ignore */ }
        const bal = r?.data?.balance;
        if (typeof bal === 'number' && bal > 0 && bal <= 5) {
          toast.warning(lang === 'en'
            ? `Only ${bal} point(s) left.`
            : `Nur noch ${bal} Punkt(e) übrig.`);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 402) {
          toast.info(lang === 'en'
            ? 'Your points are used up — top up to keep exporting.'
            : 'Deine Punkte sind aufgebraucht – lade dein Guthaben auf.');
          try { openPricing(); } catch (e) { /* ignore */ }
        }
      });
  };
  const maybePromptRate = () => {
    nudgeAfterDownload(t);
    consumeOnePoint();
    if (inviteEmail && isValidEmail(inviteEmail) && !inviteSent) {
      setInviteSent(true);
      sendTrustpilotInvitation({
        email: inviteEmail,
        name: user?.name || '',
        referenceId: `${typeId}-${Date.now()}`,
      }).then((ok) => {
        if (ok) toast.success(lang === 'en' ? 'Review invitation sent — thank you!' : 'Bewertungseinladung gesendet – danke!');
      });
    }
  };

  const handleDownload = async () => {
    if (!(await ensureQuota())) return;
    if (!result) return;
    const ext = result.format === 'svg' ? 'svg' : 'png';
    const nameBase = (isQr && qrContent !== 'text' && qrName) ? qrName : value;
    downloadDataUrl(result.data_url, `${safeName(nameBase)}_${typeId}.${ext}`);
    track('barcode_exported', { type: typeId, format: ext, source: 'download' });
    if (onSaved) onSaved();
    toast.success(t('saved_to_history'));
    maybePromptRate();
  };

  const handleCopy = async () => {
    // Copying the payload text you typed is always free (not a barcode output).
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('copied'));
    } catch (e) { console.error('Clipboard copy failed:', e); }
  };

  // Text-to-speech via the browser's built-in Web Speech API (no server/keys).
  // Reads the entered/generated barcode content aloud; click again to stop.
  const [speaking, setSpeaking] = useState(false);
  const handleSpeak = () => {
    try {
      const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
      if (!synth || typeof window.SpeechSynthesisUtterance === 'undefined') {
        toast.error(lang === 'en'
          ? 'Your browser does not support speech output.'
          : 'Dein Browser unterstützt keine Sprachausgabe.');
        return;
      }
      if (synth.speaking || synth.pending) { synth.cancel(); setSpeaking(false); return; }
      const text = (value || '').trim();
      if (!text) return;
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = lang === 'en' ? 'en-US' : 'de-DE';
      u.rate = 0.95;
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      setSpeaking(true);
      synth.speak(u);
    } catch (e) { setSpeaking(false); }
  };
  useEffect(() => () => {
    try { if (window.speechSynthesis && window.speechSynthesis.speaking) window.speechSynthesis.cancel(); } catch (e) { /* noop */ }
  }, []);

  const useExample = () => { if (currentType?.example) setValue(currentType.example); };
  const resetOptions = () => setOptions(DEFAULT_OPTIONS);

  // Payload coming from the structured QR forms (Wi-Fi / bank transfer).
  const handlePayload = (payload, name, meta) => {
    setValue(payload);
    setQrName(name || '');
    setQrMeta(meta || null);
    setError('');
  };

  const handleQrContentChange = (v) => {
    setQrContent(v);
    setValue('');
    setQrName('');
    setQrMeta(null);
    setError('');
  };

  const handlePrintWifiCard = async () => {
    if (!(await ensureQuota())) return;
    if (!value.trim() || !qrMeta || qrMeta.kind !== 'wifi') return;
    let pngDataUrl = result?.format === 'png' ? result.data_url : null;
    if (!pngDataUrl) {
      try {
        const res = await api.generate({ type: typeId, data: value, format: 'png', options });
        pngDataUrl = res.data.data_url;
      } catch (e) {
        toast.error(t('err_generate'));
        return;
      }
    }
    printWifiCard(pngDataUrl, {
      title: t('wifi_card_title'),
      instruction: t('wifi_card_instruction'),
      networkLabel: t('wifi_card_network'),
      passwordLabel: t('wifi_password'),
      ssid: qrMeta.ssid,
      password: qrMeta.password,
    });
  };

  // Switching type: clear stale error and, when the current value is empty or we
  // cross the 2D<->1D boundary (e.g. QR text -> numeric EAN), prefill the new
  // type's example so a valid preview always appears instead of an error.
  const handleTypeChange = (newTypeId) => {
    const newDef = types.find((x) => x.id === newTypeId);
    const crossedCategory = currentType && newDef && currentType.category !== newDef.category;
    setError('');
    setTypeId(newTypeId);
    // Special QR content forms only apply to QR codes.
    if (newDef?.id !== 'qrcode' && qrContent !== 'text') {
      setQrContent('text');
      setQrName('');
      setValue('');
      return;
    }
    if (qrContent === 'text' && newDef?.example && (!value.trim() || crossedCategory)) {
      setValue(newDef.example);
    }
  };

  const handlePrint = async () => {
    if (!(await ensureQuota())) return;
    if (!result) return;
    const cap = (isQr && qrContent !== 'text' && qrName) ? qrName : value;
    printImages([{ data_url: result.data_url, label: cap }], `${typeId} — ${cap}`);
    track('barcode_exported', { type: typeId, format: 'print', source: 'print' });
    maybePromptRate();
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const handlePdf = async () => {
    if (!(await ensureQuota())) return;
    if (!value.trim()) return;
    setPdfLoading(true);
    try {
      let pngDataUrl = result?.format === 'png' ? result.data_url : null;
      if (!pngDataUrl) {
        const res = await api.generate({ type: typeId, data: value, format: 'png', options });
        pngDataUrl = res.data.data_url;
      }
      const cap = (isQr && qrContent !== 'text' && qrName) ? qrName : value;
      await generateSingleBarcodePdf(pngDataUrl, {
        caption: cap,
        filename: `${safeName(cap)}_${typeId}.pdf`,
      });
      track('barcode_exported', { type: typeId, format: 'pdf', source: 'pdf' });
      maybePromptRate();
    } catch (e) {
      toast.error(t('err_generate'));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="lg:grid lg:grid-cols-12 lg:gap-6">
      {/* LEFT: form + customization */}
      <div className="space-y-4 lg:col-span-7">
        <Card data-testid="presets-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Bookmark className="h-4 w-4 text-primary" />{t('presets')}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => { setPresetName(''); setSaveOpen(true); }}
                data-testid="preset-save-open"
              >
                <BookmarkPlus className="h-3.5 w-3.5" />{t('preset_save')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {presets.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="presets-empty">
                {t('presets_hint')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="presets-list">
                {presets.map((p) => (
                  <span
                    key={p.id}
                    className="group inline-flex items-center gap-1 rounded-full border bg-secondary/40 py-1 pl-3 pr-1 text-xs transition-colors hover:border-primary"
                    data-testid={`preset-chip-${p.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => applyPreset(p)}
                      className="max-w-[180px] truncate font-medium text-foreground hover:text-primary"
                      title={p.name}
                      data-testid={`preset-load-${p.id}`}
                    >
                      {p.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => sharePreset(p)}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      aria-label={t('preset_share')}
                      title={t('preset_share')}
                      data-testid={`preset-share-${p.id}`}
                    >
                      <Share2 className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openQr(p)}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      aria-label={t('preset_qr')}
                      title={t('preset_qr')}
                      data-testid={`preset-qr-${p.id}`}
                    >
                      <QrCode className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetToDelete(p)}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label={t('delete')}
                      data-testid={`preset-delete-${p.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">1</span>
              {t('lbl_type')} & {t('lbl_value')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">{t('lbl_type')}</Label>
              <Select value={typeId} onValueChange={handleTypeChange}>
                <SelectTrigger aria-label={t('lbl_type')} data-testid="barcode-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectGroup>
                    <SelectLabel>{t('cat_2d')}</SelectLabel>
                    {grouped.twod.map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.label[lang] || x.label.en}</SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>{t('cat_1d')}</SelectLabel>
                    {grouped.oned.map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.label[lang] || x.label.en}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {currentType && (
                <p className="text-xs text-muted-foreground" data-testid="barcode-type-hint">
                  {currentType.description[lang] || currentType.description.en}{' • '}{currentType.hint[lang] || currentType.hint.en}
                </p>
              )}
            </div>

            {isQrCode && (
              <div className="space-y-1.5">
                <Label className="text-sm">{t('qr_content')}</Label>
                <Select value={qrContent} onValueChange={handleQrContentChange}>
                  <SelectTrigger aria-label={t('qr_content')} data-testid="qr-content-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">{t('content_text')}</SelectItem>
                    <SelectItem value="wifi">{t('content_wifi')}</SelectItem>
                    <SelectItem value="bank">{t('content_bank')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isQrCode && qrContent !== 'text' ? (
              <QrContentPanel contentType={qrContent} onPayload={handlePayload} />
            ) : (
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                  <Label className="text-sm">{t('lbl_value')}</Label>
                  {currentType?.example && (
                    <button onClick={useExample} className="inline-flex min-w-0 max-w-full items-baseline gap-1 text-xs font-medium text-primary hover:underline" data-testid="use-example-button">
                      <span className="shrink-0">{t('use_example')}:</span>
                      <span className="truncate font-mono">{currentType.example}</span>
                    </button>
                  )}
                </div>
                {isQr ? (
                  <Textarea
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={t('value_placeholder')}
                    rows={3}
                    data-testid="barcode-payload-input"
                  />
                ) : (
                  <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={t('value_placeholder')}
                    data-testid="barcode-payload-input"
                    className="font-mono"
                  />
                )}
              </div>
            )}

            {error && (
              <Alert variant="destructive" data-testid="barcode-payload-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>{error}</span>
                  {currentType?.example && (
                    <button
                      onClick={useExample}
                      data-testid="error-use-example-button"
                      className="font-medium underline underline-offset-2 hover:opacity-80"
                    >
                      {t('use_example')}
                    </button>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">2</span>
              {t('customization')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={resetOptions} data-testid="reset-options-button" className="h-8 gap-1.5 text-xs">
              <RotateCcw className="h-3.5 w-3.5" />{t('reset')}
            </Button>
          </CardHeader>
          <CardContent>
            <CustomizationPanel
              options={options}
              setOptions={setOptions}
              isQr={isQr}
              supportsText={supportsText}
              format={format}
              setFormat={setFormat}
            />
          </CardContent>
        </Card>
      </div>

      {/* RIGHT: preview */}
      <div className="mt-4 lg:col-span-5 lg:mt-0">
        <div className="lg:sticky lg:top-24">
          <Card data-testid="barcode-preview-container">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">3</span>
                {t('preview')}
                {currentType && <Badge variant="secondary" className="ml-auto font-normal">{currentType.label[lang] || currentType.label.en}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="barcode-checker flex min-h-[240px] items-center justify-center rounded-xl border p-6">
                {loading ? (
                  <Skeleton className="h-40 w-56" />
                ) : result ? (
                  <div className="rounded-md bg-white p-3 shadow-sm" style={{ background: options.bg_color }}>
                    <img
                      src={result.data_url}
                      alt={`${typeId} ${value}`}
                      data-testid="barcode-preview-image"
                      className="max-h-[300px] max-w-full"
                    />
                  </div>
                ) : (
                  <div className="text-center" data-testid="barcode-preview-empty">
                    <Wand2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                    <p className="mx-auto max-w-xs text-sm text-muted-foreground">{t('preview_empty')}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Button onClick={handleDownload} disabled={!result} data-testid="barcode-download-button" className="w-full gap-2">
                  <Download className="h-4 w-4" />{format === 'svg' ? t('download_svg') : t('download_png')}
                </Button>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Button variant="outline" onClick={handlePrint} disabled={!result} data-testid="barcode-print-button" className="gap-1.5">
                    <Printer className="h-4 w-4" />{t('print')}
                  </Button>
                  <Button variant="outline" onClick={handlePdf} disabled={!result || pdfLoading} data-testid="barcode-pdf-button" className="gap-1.5">
                    <FileText className="h-4 w-4" />{pdfLoading ? t('saving_pdf') : t('pdf')}
                  </Button>
                  <Button variant="outline" onClick={handleCopy} disabled={!value} data-testid="barcode-copy-payload-button" className="gap-1.5">
                    <Copy className="h-4 w-4" />{t('copy_payload')}
                  </Button>
                  <Button variant="outline" onClick={handleSpeak} disabled={!value} data-testid="barcode-speak-button" className="gap-1.5" aria-pressed={speaking}>
                    {speaking ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    {speaking ? (lang === 'en' ? 'Stop' : 'Stopp') : (lang === 'en' ? 'Read aloud' : 'Vorlesen')}
                  </Button>
                </div>
                {result && (
                  <div className="rounded-lg border border-dashed bg-muted/30 p-3" data-testid="review-invite-box">
                    <label htmlFor="review-invite-email" className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Star className="h-3.5 w-3.5 text-amber-500" />
                      {lang === 'en'
                        ? 'Get a review invite by email (optional)'
                        : 'Bewertungseinladung per E-Mail (optional)'}
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="review-invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => { setInviteEmail(e.target.value); setInviteSent(false); }}
                        placeholder={lang === 'en' ? 'you@example.com' : 'du@beispiel.de'}
                        className="pl-9"
                        data-testid="review-invite-email"
                        aria-invalid={inviteEmail ? !isValidEmail(inviteEmail) : undefined}
                      />
                    </div>
                    <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                      {lang === 'en'
                        ? 'When set, we send a Trustpilot review invitation after your next export.'
                        : 'Wenn ausgefüllt, senden wir nach deinem nächsten Export eine Trustpilot-Einladung.'}
                    </p>
                  </div>
                )}
                {!user && (
                  <p className="text-center text-xs text-muted-foreground" data-testid="free-quota-hint">
                    {freeLeft > 0
                      ? (lang === 'en'
                          ? `Free: ${freeLeft} of ${freeLimit} generations left today`
                          : `Kostenlos: noch ${freeLeft} von ${freeLimit} Generierungen heute`)
                      : (lang === 'en'
                          ? 'Free daily limit reached — sign in for unlimited access'
                          : 'Tageslimit erreicht – melde dich an für unbegrenzten Zugriff')}
                  </p>
                )}
                {isQrCode && qrContent === 'wifi' && (
                  <Button
                    variant="secondary"
                    onClick={handlePrintWifiCard}
                    disabled={!result}
                    data-testid="print-wifi-card-button"
                    className="w-full gap-2"
                  >
                    <Wifi className="h-4 w-4" />{t('print_wifi_card')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {!isQr && supportsText && options.show_text && (options.font_bold || options.scale_text) && value.trim() && (
            <ComparisonPreview
              typeId={typeId}
              value={value}
              baseOptions={{ ...options, font_bold: false, scale_text: false, font_size: DEFAULT_OPTIONS.font_size }}
              currentOptions={options}
            />
          )}
        </div>
      </div>

      {/* Save preset dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="preset-save-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="h-5 w-5 text-primary" />{t('preset_save_title')}
            </DialogTitle>
            <DialogDescription>{t('presets_hint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="preset-name" className="text-sm">{t('preset_name')}</Label>
            <Input
              id="preset-name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder={t('preset_name_placeholder')}
              autoFocus
              maxLength={80}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
              data-testid="preset-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} data-testid="preset-save-cancel">
              {t('cancel')}
            </Button>
            <Button onClick={handleSavePreset} disabled={savingPreset} className="gap-1.5" data-testid="preset-save-confirm">
              {savingPreset ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share-link QR dialog */}
      <Dialog open={!!qrPreset} onOpenChange={(o) => { if (!o) { setQrPreset(null); setQrDataUrl(''); } }}>
        <DialogContent data-testid="preset-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />{t('preset_qr_title')}
            </DialogTitle>
            <DialogDescription>{t('preset_qr_desc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-1">
            <div className="rounded-xl border bg-white p-3">
              {qrLoading ? (
                <div className="flex h-[220px] w-[220px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={qrPreset?.name || 'QR'}
                  className="h-[220px] w-[220px]"
                  data-testid="preset-qr-image"
                />
              ) : (
                <div className="flex h-[220px] w-[220px] items-center justify-center text-sm text-muted-foreground">—</div>
              )}
            </div>
            <Input
              readOnly
              value={qrPreset?._link || ''}
              onFocus={(e) => e.target.select()}
              className="font-mono text-xs"
              data-testid="preset-qr-link"
            />
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => copyLink(qrPreset?._link)} className="gap-1.5" data-testid="preset-qr-copy">
              <Copy className="h-4 w-4" />{t('copy_link')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadQrPdf} disabled={!qrDataUrl || qrPdfLoading} className="gap-1.5" data-testid="preset-qr-pdf">
                <FileText className="h-4 w-4" />{qrPdfLoading ? t('saving_pdf') : t('pdf')}
              </Button>
              <Button onClick={downloadQr} disabled={!qrDataUrl} className="gap-1.5" data-testid="preset-qr-download">
                <Download className="h-4 w-4" />{t('download_png')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete preset confirmation */}
      <AlertDialog open={!!presetToDelete} onOpenChange={(o) => { if (!o) setPresetToDelete(null); }}>
        <AlertDialogContent data-testid="preset-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('preset_delete_confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('preset_delete_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePreset} data-testid="preset-delete-confirm">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
